import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import { copyTextWithStatus } from '../../../utils/clipboard';
import WbSkuHistory from './WbSkuHistory';
import WbDailySummary from './WbDailySummary';
import './WbMissedSales.css';
import { fmtDate } from '../../../utils/ruDates';

function getTodayIso() {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function getDefaultPeriods() {
  const period2To = shiftDate(getTodayIso(), -1);
  const period2From = shiftDate(period2To, -6);
  const period1To = shiftDate(period2From, -1);
  const period1From = shiftDate(period1To, -6);
  return { period1From, period1To, period2From, period2To };
}

function getSavedPeriods() {
  try {
    const saved = localStorage.getItem('wb-missed-sales-periods');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.period1From && parsed.period1To && parsed.period2From && parsed.period2To) {
        return parsed;
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

function savePeriods(periods) {
  try {
    localStorage.setItem('wb-missed-sales-periods', JSON.stringify(periods));
  } catch (e) {
    // Ignore localStorage errors
  }
}

function getDiagnosisIcon(code) {
  const icons = {
    TRAFFIC_DROP: '↓',
    CARD_CONVERSION_DROP: '◈',
    CHECKOUT_DROP: '→',
    BUYOUT_DROP: '□',
    RETURNS_GROWTH: '↩',
    LOCALIZATION_DROP: '⌂',
    AVG_CHECK_DROP: '₽',
    REVENUE_DROP: '!',
  };
  return icons[code] || '•';
}

function formatCurrency(value) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function normalizeError(error) {
  return error?.message || 'Не удалось загрузить данные';
}

export default function WbMissedSales({ accountId, accountName }) {
  const { request, showStatus } = useApi();
  const defaults = useMemo(() => getSavedPeriods() || getDefaultPeriods(), []);
  const [period1From, setPeriod1From] = useState(defaults.period1From);
  const [period1To, setPeriod1To] = useState(defaults.period1To);
  const [period2From, setPeriod2From] = useState(defaults.period2From);
  const [period2To, setPeriod2To] = useState(defaults.period2To);
  const [loading, setLoading] = useState(false);
  const [creatingTaskSku, setCreatingTaskSku] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [historySku, setHistorySku] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState(''); // 'critical', 'warning', 'info', or ''
  const [filterDiagnosis, setFilterDiagnosis] = useState(''); // diagnosis code or ''
  const [groupByDiagnosis, setGroupByDiagnosis] = useState(false); // Toggle grouped view
  const getWhyLines = (item) => (Array.isArray(item?.why) ? item.why : []);

  const payload = useMemo(() => ({
    period1_from: `${period1From}T00:00:00`,
    period1_to: `${period1To}T23:59:59`,
    period2_from: `${period2From}T00:00:00`,
    period2_to: `${period2To}T23:59:59`,
  }), [period1From, period1To, period2From, period2To]);

  const loadMissedSales = async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await request(`/api/v1/wb-analytics/${accountId}/missed-sales`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setData(response);
    } catch (err) {
      const message = normalizeError(err);
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accountId) return;
    loadMissedSales();
  }, [accountId]);

  useEffect(() => {
    savePeriods({ period1From, period1To, period2From, period2To });
  }, [period1From, period1To, period2From, period2To]);

  const handleCreateTask = async (item) => {
    if (!accountId || !item?.sku) return;
    setCreatingTaskSku(item.sku);
    try {
      const task = await request(`/api/v1/wb-analytics/${accountId}/missed-sales/${item.sku}/task`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      showStatus?.('success', `Задача создана: ${task.title}`);
    } catch (err) {
      showStatus?.('error', normalizeError(err));
    } finally {
      setCreatingTaskSku('');
    }
  };

  const filteredItems = useMemo(() => {
    if (!Array.isArray(data?.items)) return [];
    return data.items.filter((item) => {
      if (filterSeverity && item.severity !== filterSeverity) return false;
      if (filterDiagnosis && item.diagnosis_code !== filterDiagnosis) return false;
      return true;
    });
  }, [data?.items, filterSeverity, filterDiagnosis]);

  const uniqueDiagnoses = useMemo(() => {
    if (!Array.isArray(data?.items)) return [];
    const codes = new Set(data.items.map((item) => item.diagnosis_code));
    return Array.from(codes).sort();
  }, [data?.items]);

  const groupDiagnostics = useMemo(() => {
    if (!Array.isArray(filteredItems)) return {};
    const groups = {};
    filteredItems.forEach((item) => {
      const key = item.diagnosis_code;
      if (!groups[key]) {
        groups[key] = { title: item.diagnosis_title, items: [] };
      }
      groups[key].items.push(item);
    });
    // Sort by total missed revenue
    const sorted = Object.entries(groups).sort(
      (a, b) =>
        b[1].items.reduce((sum, item) => sum + item.missed_revenue, 0) -
        a[1].items.reduce((sum, item) => sum + item.missed_revenue, 0)
    );
    return Object.fromEntries(sorted);
  }, [filteredItems]);

  const renderSkuCard = (item) => (
    <article key={item.sku} className="sku-card">
      <div className="sku-card__image">
        {item.image ? (
          <img src={item.image} alt={item.name} />
        ) : (
          <div className="sku-card__image--placeholder">WB</div>
        )}
      </div>

      <div className="sku-card__content">
        <div className="sku-card__header">
          <div className="sku-card__title">
            <span className="sku-card__sku">{item.offer_id || `SKU ${item.sku}`}</span>
            <span className="sku-card__name">{item.name || item.sku}</span>
            {item.brand_name && <span className="sku-card__brand">{item.brand_name}</span>}
            {item.subject_name && <span className="sku-card__category">{item.subject_name}</span>}
          </div>
          <div>
            <div className="sku-card__revenue">{formatCurrency(item.missed_revenue)}</div>
            <div className="sku-card__orders">{formatNumber(item.missed_orders)} заказов</div>
          </div>
        </div>

        <div className="sku-card__diagnosis">
          <span className="sku-card__diagnosis-icon">{getDiagnosisIcon(item.diagnosis_code)}</span>
          <div className="sku-card__diagnosis-text">
            <div className="sku-card__diagnosis-label">Диагноз</div>
            <div>{item.diagnosis_title}</div>
            <div className="sku-card__metrics">
              {getWhyLines(item).map((line) => (
                <span key={line} className="sku-card__metric-chip">{line}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="sku-card__action">
          <span className="sku-card__action-icon">✓</span>
          <div className="sku-card__action-text">
            <div className="sku-card__action-label">План действий</div>
            <ul className="sku-card__action-list">
              {(item.action_plan || []).map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="sku-card__buttons">
          <button
            type="button"
            className="sku-card__button sku-card__button--small"
            onClick={() => handleCopySku(item)}
            title="Скопировать SKU и Offer ID"
          >
            📋 Копировать
          </button>
          <button
            type="button"
            className="sku-card__button"
            onClick={() => handleCreateTask(item)}
            disabled={creatingTaskSku === item.sku}
          >
            {creatingTaskSku === item.sku ? 'Создаём...' : 'Создать задачу'}
          </button>
          <button
            type="button"
            className="sku-card__button"
            onClick={() => setHistorySku(item)}
          >
            История SKU
          </button>
        </div>
      </div>
    </article>
  );

  const handleCopySku = async (item) => {
    const textToCopy = `${item.sku} ${item.offer_id || ''}`.trim();
    try {
      await copyTextWithStatus(textToCopy, 'SKU', showStatus);
    } catch (err) {
      showStatus?.('error', 'Не удалось скопировать');
    }
  };

  const handlePrintReport = () => {
    const summary = data?.summary || {};
    const reportContent = `
Упущенные продажи WB - Отчёт ${accountName ? `(${accountName})` : ''}
${fmtDate()}

СВОДКА
Потерянная выручка: ${formatCurrency(summary.missed_revenue_total)}
Потерянные заказы: ${formatNumber(summary.missed_orders_total)}
Критичных SKU: ${formatNumber(summary.critical_items)}

РЕЗУЛЬТАТЫ (${filteredItems.length} товаров)
${filteredItems.map((item, idx) => `
${idx + 1}. ${item.name}
   SKU: ${item.sku} | Offer: ${item.offer_id}
   Потеря: ${formatCurrency(item.missed_revenue)} (${formatNumber(item.missed_orders)} заказов)
   Диагноз: ${item.diagnosis_title}
   ${getWhyLines(item).join(' | ')}
`).join('')}
    `;
    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(reportContent)}`);
    element.setAttribute('download', `wb-missed-sales-${new Date().toISOString().split('T')[0]}.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="wb-analytics">
      <div className="wb-analytics__header">
        <div>
          <h2 className="wb-analytics__title">Упущенные продажи WB</h2>
          {accountName ? <div className="wb-analytics__subtitle">{accountName}</div> : null}
        </div>
        <div className="wb-analytics__controls">
          <button
            type="button"
            className="wb-analytics__button wb-analytics__button--primary"
            onClick={loadMissedSales}
            disabled={loading || !accountId}
          >
            {loading ? 'Обновляем...' : 'Обновить анализ'}
          </button>
          <button
            type="button"
            className="wb-analytics__button"
            onClick={() => {
              const newDefaults = getDefaultPeriods();
              setPeriod1From(newDefaults.period1From);
              setPeriod1To(newDefaults.period1To);
              setPeriod2From(newDefaults.period2From);
              setPeriod2To(newDefaults.period2To);
            }}
            title="Сбросить периоды на стандартные значения"
          >
            Сбросить периоды
          </button>
          {data && filteredItems.length > 0 && (
            <>
              <button
                type="button"
                className="wb-analytics__button"
                onClick={() => setGroupByDiagnosis(!groupByDiagnosis)}
                title="Группировать по типу диагноза"
              >
                {groupByDiagnosis ? '📋 По SKU' : '🏷 По диагнозу'}
              </button>
              <button
                type="button"
                className="wb-analytics__button"
                onClick={handlePrintReport}
                title="Скачать отчёт в формате текста"
              >
                📥 Скачать отчёт
              </button>
            </>
          )}
        </div>
      </div>

      {accountId && <WbDailySummary accountId={accountId} />}

      <div className="wb-analytics__filters">
        <div className="wb-analytics__filter-group">
          <label className="wb-analytics__label">Период 1</label>
          <div className="wb-analytics__date-row">
            <input type="date" value={period1From} onChange={(e) => setPeriod1From(e.target.value)} />
            <span>—</span>
            <input type="date" value={period1To} onChange={(e) => setPeriod1To(e.target.value)} />
          </div>
        </div>
        <div className="wb-analytics__filter-group">
          <label className="wb-analytics__label">Период 2</label>
          <div className="wb-analytics__date-row">
            <input type="date" value={period2From} onChange={(e) => setPeriod2From(e.target.value)} />
            <span>—</span>
            <input type="date" value={period2To} onChange={(e) => setPeriod2To(e.target.value)} />
          </div>
        </div>
        <div className="wb-analytics__filter-group">
          <label className="wb-analytics__label">Тяжесть</label>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className="wb-analytics__select">
            <option value="">Все уровни</option>
            <option value="critical">🔴 Критично</option>
            <option value="warning">🟡 Внимание</option>
            <option value="info">🔵 Информация</option>
          </select>
        </div>
        <div className="wb-analytics__filter-group">
          <label className="wb-analytics__label">Диагноз</label>
          <select value={filterDiagnosis} onChange={(e) => setFilterDiagnosis(e.target.value)} className="wb-analytics__select">
            <option value="">Все диагнозы</option>
            {uniqueDiagnoses.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="wb-loading">
          <div className="wb-loading__spinner" />
          <p className="wb-loading__text">Собираем compare, диагнозы и action plan по SKU...</p>
        </div>
      ) : null}

      {!loading && error ? (
        <div className="wb-error">
          <p className="wb-error__title">Ошибка загрузки</p>
          <p className="wb-error__description">{error}</p>
        </div>
      ) : null}

      {!loading && !error && data ? (
        <>
          <div className="wb-summary">
            <div className="wb-summary__header">
              <h3 className="wb-summary__title">Сводка по потерям</h3>
            </div>

            <div className="wb-summary__metric">
              <div className="wb-summary__metric-label">Потерянная выручка</div>
              <div className="wb-summary__metric-value">{formatCurrency(data.summary?.missed_revenue_total)}</div>
            </div>

            <div className="wb-summary__metric">
              <div className="wb-summary__metric-label">Потерянные заказы</div>
              <div className="wb-summary__metric-value">{formatNumber(data.summary?.missed_orders_total)}</div>
            </div>

            <div className="wb-summary__metric">
              <div className="wb-summary__metric-label">Критичных SKU</div>
              <div className="wb-summary__metric-value">{formatNumber(data.summary?.critical_items)}</div>
            </div>

            <div className="wb-summary__action-count">
              {formatNumber(data.summary?.critical_items)} SKU требуют проверки и действий
            </div>
          </div>

          <div className="wb-analytics__items">
            {filteredItems && filteredItems.length > 0 ? (
              groupByDiagnosis
                ? Object.entries(groupDiagnostics).map(([code, group]) => (
                    <div key={code} className="diagnosis-group">
                      <div className="diagnosis-group__header">
                        <span className="diagnosis-group__icon">{getDiagnosisIcon(code)}</span>
                        <div className="diagnosis-group__info">
                          <h3 className="diagnosis-group__title">{group.title}</h3>
                          <p className="diagnosis-group__count">
                            {group.items.length} товаров • Потеря: {formatCurrency(
                              group.items.reduce((sum, item) => sum + item.missed_revenue, 0)
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="diagnosis-group__items">
                        {group.items.map((item) => renderSkuCard(item))}
                      </div>
                    </div>
                  ))
                : filteredItems.map((item) => renderSkuCard(item))
            ) : (
              <div className="wb-empty">
                <div className="wb-empty__icon">∅</div>
                <h3 className="wb-empty__title">Потерь по выбранным периодам не найдено</h3>
                <p className="wb-empty__description">
                  Попробуйте расширить период или сравнить другой диапазон дат.
                </p>
              </div>
            )}
          </div>
        </>
      ) : null}

      {historySku ? (
        <div className="wb-history-modal">
          <div className="wb-history-modal__backdrop" onClick={() => setHistorySku(null)} />
          <div className="wb-history-modal__dialog">
            <WbSkuHistory
              accountId={accountId}
              sku={historySku.sku}
              item={historySku}
              onClose={() => setHistorySku(null)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
